import { z } from 'zod';
import { DEVISES } from './model/commun.js';
export const ETAPES_AFFAIRE = ['interet', 'offre', 'reservation', 'gagnee', 'perdue'];
export const ETAPES_OUVERTES = ['interet', 'offre', 'reservation'];
export const SOURCES_AFFAIRE = ['manuelle', 'demande', 'offre', 'stand', 'liste_attente'];
export const MOTIFS_PERTE = ['prix', 'deja_vendue', 'sans_suite', 'autre'];
export const ETATS_PLACE_ATTENTE = ['active', 'a_rappeler', 'close'];
export const PROBABILITES_PAR_DEFAUT = {
    interet: 10,
    offre: 40,
    reservation: 75,
    gagnee: 100,
    perdue: 0,
};
const id = z.string().regex(/^[a-f\d]{24}$/i, 'Identifiant invalide.');
const montant = z.int().min(0).max(100_000_000_000);
const probabilite = z.int().min(0).max(100);
const date = z.iso
    .date()
    .refine((s) => new Date(s).toISOString().slice(0, 10) === s, 'Date inexistante.');
export const ligneAffaireSchema = z.strictObject({
    oeuvreId: id,
    exemplaireId: id.nullish(),
    montantCentimes: montant,
});
const lignes = z
    .array(ligneAffaireSchema)
    .min(1)
    .max(20)
    .refine((valeurs) => new Set(valeurs.map((l) => `${l.oeuvreId}:${l.exemplaireId ?? ''}`)).size === valeurs.length, 'Une cible ne peut figurer deux fois.');
export const creationAffaireSchema = z.strictObject({
    contactId: id,
    titre: z.string().trim().max(5000).optional(),
    lignes,
    devise: z.enum(DEVISES).optional(),
    probabilite: probabilite.optional(),
    clotureEspereeLe: date.nullish(),
    responsableId: id.nullish(),
});
export const modificationAffaireSchema = creationAffaireSchema
    .omit({ contactId: true, devise: true })
    .partial()
    .extend({ version: z.int().min(0) })
    .refine((c) => Object.keys(c).length > 1, 'Rien à modifier.');
export const motifPerteSchema = z
    .strictObject({ code: z.enum(MOTIFS_PERTE), texte: z.string().trim().max(2000).optional() })
    .refine((c) => c.code !== 'autre' || Boolean(c.texte), 'Précisez le motif.');
export const deplacementAffaireSchema = z
    .strictObject({
    vers: z.enum(ETAPES_AFFAIRE),
    version: z.int().min(0),
    motifPerte: motifPerteSchema.optional(),
})
    .refine((c) => c.vers !== 'perdue' || c.motifPerte !== undefined, 'Le motif de perte est obligatoire.');
export const reouvertureAffaireSchema = z.strictObject({
    vers: z.enum(ETAPES_OUVERTES),
    version: z.int().min(0),
});
export const probabilitesPipelineSchema = z.strictObject({
    interet: probabilite,
    offre: probabilite,
    reservation: probabilite,
    gagnee: probabilite,
    perdue: probabilite,
});
export const reglagesPipelineSchema = z.strictObject({
    probabilites: probabilitesPipelineSchema,
    version: z.int().min(0),
});
export const creationPlaceAttenteSchema = z.strictObject({
    oeuvreId: id,
    exemplaireId: id.nullish(),
    contactId: id,
    note: z.string().trim().max(2000).optional(),
});
export const modificationPlaceAttenteSchema = z.strictObject({
    note: z.string().trim().max(2000),
    version: z.int().min(0),
});
export const ordrePlacesAttenteSchema = z.strictObject({
    oeuvreId: id,
    exemplaireId: id.nullish(),
    ids: z.array(id).refine((ids) => new Set(ids).size === ids.length, 'Identifiants répétés.'),
});
export function trimestreCivil(date, decalage = 0) {
    const debut = new Date(Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3 + decalage * 3, 1));
    const fin = new Date(Date.UTC(debut.getUTCFullYear(), debut.getUTCMonth() + 3, 1));
    return {
        debut,
        fin,
        annee: debut.getUTCFullYear(),
        numero: Math.floor(debut.getUTCMonth() / 3) + 1,
    };
}
export function calculerPrevision(affaires, factures, trimestre, maintenant) {
    const periode = trimestreCivil(maintenant, trimestre === 'suivant' ? 1 : 0);
    const precedente = trimestreCivil(periode.debut, -4);
    const dans = (date, p) => date !== null && date >= p.debut && date < p.fin;
    const liees = new Set(affaires.flatMap((a) => a.liens.factureIds));
    const chiffres = (p) => ({
        realiseCentimes: factures === null
            ? affaires
                .filter((a) => a.etape === 'gagnee' && dans(a.closeLe, p))
                .reduce((s, a) => s + a.montantCentimes, 0)
            : [...new Map(factures.map((f) => [f.id, f])).values()]
                .filter((f) => liees.has(f.id) && dans(f.emiseLe, p))
                .reduce((s, f) => s + f.totalHtCentimes, 0),
        pondereCentimes: affaires
            .filter((a) => ETAPES_OUVERTES.includes(a.etape) && dans(a.clotureEspereeLe, p))
            .reduce((s, a) => s + Math.round((a.montantCentimes * a.probabilite) / 100), 0),
        sansDateCentimes: affaires
            .filter((a) => ETAPES_OUVERTES.includes(a.etape) && a.clotureEspereeLe === null)
            .reduce((s, a) => s + a.montantCentimes, 0),
    });
    const existe = affaires.some((a) => dans(a.closeLe, precedente) || dans(a.clotureEspereeLe, precedente)) ||
        factures?.some((f) => liees.has(f.id) && dans(f.emiseLe, precedente));
    return {
        trimestre: periode,
        ...chiffres(periode),
        comparaison: existe ? chiffres(precedente) : null,
    };
}
//# sourceMappingURL=pipeline.js.map